import { __, compose, split, map, filter, includes, isEmpty } from 'lodash/fp';
import { createAction, DateService, DateTimeService, DateTimeUtils, lt, showErrorAlert } from '@mews/framework';
import { TimeUnit } from '@mews/enums';
import { CommanderThunkAction } from '@mews-commander/types';
import { ReservationPurpose, ServiceOrderState, ManualOrigin } from '@mews-commander/legacy-data-structures';
import { oneOf, isNightUse } from '@mews-commander/utils';
import { getAvailabilityBlock } from '@mews-commander/legacy-entities/src/ducks/availabilityBlocks';
import {
    getSendConfirmationEmailByDefault,
    getService,
    getServiceTimeUnit,
    getTimeUnitByServiceId,
} from '@mews-commander/legacy-entities/src/ducks/services';
import { getCurrentEnterpriseIanaTimeZoneIdentifier } from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import { getRate } from '@mews-commander/legacy-entities/src/ducks/rates';
import { getResourceCategory } from '@mews-commander/legacy-entities/src/ducks/resourceCategories';
import { getSelectedServiceProductsOfferedToEmployee, getSelectedEnterpriseServiceId } from '../selectors/entities';
import {
    isDefinite,
    getState as getReservationState,
    hasSelectedStartDateOutOfEditableHistoryWindow,
    hasDatesSelected,
    hasTimesSelected,
} from '../selectors';
import MailReceivers from '../enums/MailReceivers';
import { NEW_RESERVATION_SCREEN_EVENT_CATEGORY } from '../constants';
import { getAvailabilityBlockDateConstraints } from '../../availabilityBlock/utils/availabilityBlockDateConversions';
import { NewReservationScreenUrlParams, NewReservationScreenUrlParamsKeys } from '../schemas/urlParamsSchema';
import { changeFilters, selectResourceCategoryType, showRates } from './screens';
import { ORDER_VALUE_CHANGED, DATES_CHANGED, AVAILABILITY_BLOCK_SELECTED, TIMES_CHANGED } from './types';
import {
    changeStateValue,
    changeOrigin,
    changeReleaseDate,
    changeReleaseTime,
    changeConfirmationEmailReceiversValue,
} from './options';
import { changeProductIdsWithoutPricingCheck } from './pricing';
import { selectService } from './service';
import { processSelectedReservationPlace } from './availability';
import { changeCategoryOccupancy, changeDates } from './order';
import {
    preselectBooker,
    preselectCustomer,
    preselectPartnerCompany,
    preselectReservationGroup,
    preselectTravelAgency,
} from './searchResult';
import { selectRateId } from './rateGroups';
import { fetchRatePricing } from './api';

export const processUrlParams =
    (searchParams: NewReservationScreenUrlParams, scope: string) => async (dispatch: $TSFixMe, getState: $TSFixMe) => {
        const {
            [NewReservationScreenUrlParamsKeys.ServiceId]: ServiceId,
            // dates
            [NewReservationScreenUrlParamsKeys.StartUtc]: startUtc,
            [NewReservationScreenUrlParamsKeys.EndUtc]: endUtc,

            // search values
            [NewReservationScreenUrlParamsKeys.GroupId]: reservationGroupId,
            [NewReservationScreenUrlParamsKeys.CustomerId]: customerId,
            [NewReservationScreenUrlParamsKeys.BookerId]: bookerId,
            [NewReservationScreenUrlParamsKeys.PartnerCompanyId]: partnerCompanyId,
            [NewReservationScreenUrlParamsKeys.TravelAgencyId]: travelAgencyId,
            [NewReservationScreenUrlParamsKeys.AvailabilityBlockId]: availabilityBlockIdParam,

            // order values
            [NewReservationScreenUrlParamsKeys.ProductIds]: productIdsString,

            // enum values
            [NewReservationScreenUrlParamsKeys.State]: stateName,
            [NewReservationScreenUrlParamsKeys.Origin]: originName,
            [NewReservationScreenUrlParamsKeys.Purpose]: purposeName,

            // optional release date, applicable only if the state is optional
            [NewReservationScreenUrlParamsKeys.ReleasedUtc]: releaseUtc,

            // Params from new Reservation from Timeline to preselect bottom part of the screen
            [NewReservationScreenUrlParamsKeys.RateId]: rateId,
            [NewReservationScreenUrlParamsKeys.ResourceId]: resourceId,
            [NewReservationScreenUrlParamsKeys.ResourceCategoryId]: resourceCategoryId,
            [NewReservationScreenUrlParamsKeys.OccupancyData]: occupancyData,
        } = searchParams;

        let state = getState();

        const availabilityBlockId = !Array.isArray(availabilityBlockIdParam) ? availabilityBlockIdParam : undefined;

        const availabilityBlock = availabilityBlockId
            ? getAvailabilityBlock(getState(), { id: availabilityBlockId })
            : undefined;

        if (availabilityBlockId) {
            dispatch(createAction(AVAILABILITY_BLOCK_SELECTED, availabilityBlockId, { scope }));
        }

        const serviceId = availabilityBlock?.serviceId ?? (ServiceId as string);

        if (serviceId && !Array.isArray(serviceId)) {
            await dispatch(selectService(serviceId, scope));
        }

        if (startUtc && endUtc) {
            await dispatch(setDatesParam(serviceId, startUtc, endUtc, scope));
        }

        if (reservationGroupId) {
            dispatch(preselectReservationGroup({ id: reservationGroupId, scope }));
        }

        if (customerId) {
            dispatch(preselectCustomer({ id: customerId, scope }));
        }

        if (bookerId) {
            dispatch(preselectBooker({ id: bookerId, scope }));
        }

        if (partnerCompanyId) {
            dispatch(preselectPartnerCompany({ id: partnerCompanyId, scope }));
        }

        if (travelAgencyId) {
            dispatch(preselectTravelAgency({ id: travelAgencyId, scope, serviceId }));
        }

        if (availabilityBlock) {
            if (availabilityBlock.bookerId) {
                dispatch(preselectBooker({ id: availabilityBlock.bookerId, scope }));
            }
            if (availabilityBlock.voucherId) {
                dispatch(
                    createAction(
                        ORDER_VALUE_CHANGED,
                        { key: 'voucherId', value: availabilityBlock.voucherId },
                        { scope }
                    )
                );
            }
            if (availabilityBlock.reservationPurpose) {
                dispatch(
                    createAction(
                        ORDER_VALUE_CHANGED,
                        { key: 'purpose', value: availabilityBlock.reservationPurpose },
                        { scope }
                    )
                );
            }
            if (availabilityBlock.companyId) {
                dispatch(preselectPartnerCompany({ id: availabilityBlock.companyId, scope }));
            }

            if (availabilityBlock.travelAgencyId) {
                dispatch(preselectTravelAgency({ id: availabilityBlock.travelAgencyId, scope, serviceId }));
            }

            if (availabilityBlock?.startUtc && availabilityBlock?.endUtc) {
                state = getState();
                const service = getService(state, { id: availabilityBlock.serviceId });

                const { startDate, endDate } = getAvailabilityBlockDateConstraints(
                    availabilityBlock.startUtc,
                    availabilityBlock.endUtc,
                    getCurrentEnterpriseIanaTimeZoneIdentifier(state),
                    !!(service && isNightUse(service)),
                    getServiceTimeUnit(state, { id: availabilityBlock.serviceId })
                );

                if (startDate && endDate && DateService.isEqual(startDate, endDate)) {
                    await dispatch(
                        changeDates(
                            DateTimeUtils.dateToStructuredDate(startDate),
                            DateTimeUtils.dateToStructuredDate(endDate),
                            scope
                        )
                    );
                }
            }
        }

        if (productIdsString) {
            // state might have changed after setDatesParam dispatch, as it is fetching service
            const serviceProducts = getSelectedServiceProductsOfferedToEmployee(getState(), { scope });
            const serviceProductIds = map('id', serviceProducts);
            const existingProductIds = compose(filter(includes(__, serviceProductIds)), split(','))(productIdsString);

            if (!isEmpty(existingProductIds)) {
                dispatch(changeProductIdsWithoutPricingCheck(existingProductIds, scope));
                dispatch(fetchRatePricing({ scope, origin: 'NRS-processUrlParams' }));
            }
        }

        if (originName) {
            // @ts-expect-error $TSFixMeError: Argument of type 'SearchParam' is not assignable to parameter of type '"InPerson" | "Channel" | "Phone" | "Email" | "Web...
            if (ManualOrigin.getByName(originName)) {
                dispatch(changeOrigin(originName, scope));
            }
        }

        if (stateName) {
            if (
                oneOf(
                    stateName,
                    ServiceOrderState.Confirmed.name,
                    ServiceOrderState.Optional.name,
                    ServiceOrderState.Inquired.name
                )
            ) {
                // @ts-expect-error
                dispatch(changeStateValue(stateName, scope));
            }
        }

        if (purposeName) {
            // @ts-expect-error $TSFixMeError: Argument of type 'SearchParam' is not assignable to parameter of type 'ReservationPurpose'.
            if (ReservationPurpose.getByName(purposeName)) {
                dispatch(createAction(ORDER_VALUE_CHANGED, { key: 'purpose', value: purposeName }, { scope }));
            }
        }

        const resourceCategory = resourceCategoryId
            ? getResourceCategory(getState(), { id: resourceCategoryId, serviceId })
            : null;

        if (resourceCategory) {
            await dispatch(selectResourceCategoryType(resourceCategory.type, scope));
        }

        if (resourceId) {
            await dispatch(changeFilters({ filterValues: { selectedResourceId: resourceId }, scope }));
        }

        if (rateId) {
            const rate = getRate(getState(), { rateId });

            if (rate) {
                await dispatch(selectRateId(rate.rateGroupId, rateId, scope));
            }
        }

        if (occupancyData && resourceId && resourceCategory) {
            dispatch(
                changeCategoryOccupancy(
                    {
                        categoryId: resourceCategory.id,
                        occupancyData: Object.entries(occupancyData).reduce(
                            (acc, [ageCategoryId, personCount]) => ({
                                ...acc,
                                [ageCategoryId]: { ageCategoryId, personCount },
                            }),
                            {}
                        ),
                    },
                    scope
                )
            );
        }

        // State has potentially changed, we need a fresh state
        state = getState();

        if (releaseUtc && !isDefinite(state, { scope })) {
            dispatch(setReleaseDateTimeParam(releaseUtc, scope));
        }

        const timeUnit = getTimeUnitByServiceId(state, { serviceId });

        if (
            serviceId &&
            (timeUnit === TimeUnit.Hour
                ? hasDatesSelected(state, { scope }) && hasTimesSelected(state, { scope })
                : hasDatesSelected(state, { scope }))
        ) {
            dispatch(showRates(scope));
        }
    };

const setDatesParam =
    (urlServiceId: string, startUtc: string, endUtc: string, scope: string): CommanderThunkAction =>
    async (dispatch, getState, { loggerService, appClockService }) => {
        const serviceId = urlServiceId || getSelectedEnterpriseServiceId(getState(), { scope });
        const timeUnit = getTimeUnitByServiceId(getState(), { serviceId });

        try {
            const timezone = getCurrentEnterpriseIanaTimeZoneIdentifier(getState());
            const startDateString = DateTimeUtils.dateTimeUtcStringToDate(startUtc, timezone).value;
            const endDateString = DateTimeUtils.dateTimeUtcStringToDate(endUtc, timezone).value;
            const startTimeString = DateTimeUtils.dateTimeUtcStringToTime(startUtc, timezone).value;
            const endTimeString = DateTimeUtils.dateTimeUtcStringToTime(endUtc, timezone).value;
            const props = { scope };
            dispatch(createAction(DATES_CHANGED, { startDate: startDateString, endDate: endDateString }, { scope }));
            if (timeUnit === TimeUnit.Hour) {
                dispatch(
                    createAction(TIMES_CHANGED, { startTime: startTimeString, endTime: endTimeString }, { scope })
                );
            }
            await dispatch(processSelectedReservationPlace(scope));

            const state = getState();

            // check for EHW
            if (hasSelectedStartDateOutOfEditableHistoryWindow(state, { serviceId, scope, appClockService })) {
                showErrorAlert({ description: lt('StartDateInPastErrorMessage') });
                return;
            }

            const sendConfirmation = getSendConfirmationEmailByDefault(state, { id: serviceId });
            const confirmationType =
                sendConfirmation && getReservationState(state, props) !== ServiceOrderState.Inquired.name
                    ? MailReceivers.CustomerAndBooker
                    : MailReceivers.None;
            dispatch(changeConfirmationEmailReceiversValue(confirmationType.value, scope));
        } catch (e) {
            loggerService.log(NEW_RESERVATION_SCREEN_EVENT_CATEGORY, 'set-date-params-failed', { serviceId });
        }
    };

const setReleaseDateTimeParam =
    (releaseUtc: $TSFixMe, scope: $TSFixMe): CommanderThunkAction =>
    (dispatch, getState, { container, loggerService }) => {
        const { appLocalizationComponent } = container();

        try {
            const timezone = getCurrentEnterpriseIanaTimeZoneIdentifier(getState());
            const releaseDateTime = DateTimeService.fromUtcString(releaseUtc, timezone);
            const releaseDate = appLocalizationComponent.localizeDate(releaseDateTime);
            const releaseTime = appLocalizationComponent.localizeShortTime(releaseDateTime);

            dispatch(changeReleaseDate(releaseDate, scope));
            dispatch(changeReleaseTime(releaseTime, scope));
        } catch (e) {
            loggerService.log(NEW_RESERVATION_SCREEN_EVENT_CATEGORY, 'set-release-dateTime-failed');
        }
    };
